import type { ModuleMigrationDef } from "@kubdee/sdk";
import type { CapabilityName } from "../capabilities";

// Issue #249 M7: typed constant — a typo becomes a compile error.
const CAPABILITIES = JSON.stringify(["email"] satisfies CapabilityName[]);

/**
 * Seed the `gmail` connector type row (P9 — issue #243).
 *
 * - Idempotent via `INSERT OR IGNORE`: re-running the migration won't clobber
 *   operator edits (icon / label tweaks, scope additions).
 * - Primary key `id` equals `slug` so the capability registry and webhook
 *   ingress route both address the row by the same string. This matches the
 *   Facebook Messenger / Line / Instagram seeds (migrations 002–004).
 * - `oauth_config` carries the standard Google OAuth 2.0 endpoints. Gmail
 *   clients are CONFIDENTIAL — `clientSecret` is required for both
 *   `authorization_code` and `refresh_token` grants. Default scopes cover
 *   the adapter's full method surface (send + readonly + modify); operators
 *   can narrow them via the admin UI if they only need a subset.
 * - `webhook_config` records the Pub/Sub push transport + bearer-token auth
 *   model the adapter enforces. Operators configure a Google Cloud Pub/Sub
 *   topic that grants publish access to
 *   `gmail-api-push@system.gserviceaccount.com`, then wire a push
 *   subscription at the ingress URL with an `Authorization: Bearer <token>`
 *   header OR a `?token=<token>` query param. The token lives on the
 *   instance config (`webhook_bearer_token` field) and is bound to ONE
 *   instance. Rotating it requires updating the Pub/Sub subscription too.
 *   We document the Google identity-JWT alternative but don't require it —
 *   simpler + equally secure with the per-instance bearer primitive.
 * - `icon: 'Mail'` — surfaced via `packages/ui/src/lib/icon-registry.ts`
 *   (added to `extraIcons` alongside the seed). The connector UI reads
 *   `connectors_types.icon` + falls back to `resolveTypeIcon(slug)` if the
 *   name isn't in the registry, so even if codegen misses us the dashboard
 *   still renders a sensible glyph.
 * - `color: '#EA4335'` — Gmail brand red.
 * - `created_at` / `updated_at` use a fixed seed timestamp — tenants-local
 *   time isn't available at seed time, and the column is audit-only.
 */
export const migration: ModuleMigrationDef = {
  key: "006_gmail_seed",
  version: "0.6.0",
  sql: `
INSERT OR IGNORE INTO \`connectors_types\` (
  id, slug, name, icon, icon_url, capabilities, oauth_config, webhook_config,
  is_active, created_at, updated_at
) VALUES (
  'gmail',
  'gmail',
  'Gmail',
  'Mail',
  NULL,
  '${CAPABILITIES}',
  '{"primaryMode":"oauth","authorizeUrl":"https://accounts.google.com/o/oauth2/v2/auth","tokenUrl":"https://oauth2.googleapis.com/token","clientIdEnv":"GMAIL_CLIENT_ID","clientSecretEnv":"GMAIL_CLIENT_SECRET","scopes":["https://www.googleapis.com/auth/gmail.send","https://www.googleapis.com/auth/gmail.readonly","https://www.googleapis.com/auth/gmail.modify"],"authParams":{"access_type":"offline","prompt":"consent","include_granted_scopes":"true"},"color":"#EA4335","notes":"Google clients are confidential — clientSecret required on both authorization_code and refresh_token grants. access_type=offline + prompt=consent ensure a refresh_token is always returned."}',
  '{"transport":"pubsub-push","authMethod":"bearer-token","bearerTokenField":"webhook_bearer_token","queryParamFallback":"token","pubsubTopicField":"pubsub_topic","requiredIamBinding":"gmail-api-push@system.gserviceaccount.com must have roles/pubsub.publisher on the topic","notes":"Gmail does not POST webhooks directly. Configure a Google Cloud Pub/Sub topic + push subscription that POSTs to the ingress URL. Optionally enable Google OIDC identity-token auth on the push subscription for defence-in-depth; the adapter accepts either the OIDC-signed JWT or the per-instance bearer token (whichever the subscription is configured to send)."}',
  1,
  '2026-04-17T00:00:00Z',
  '2026-04-17T00:00:00Z'
);
  `.trim(),
};
